/**
 * InteractiveViewer — client-side renderer for an out_viz module's view-spec.
 *
 * A module wired to the operation graph's ``out_viz`` sentinel produces a
 * structured ``application/json`` view-spec ``{ view_kind, version, data }``
 * (persisted to R2, served as ``quote.view_spec_url``). This component fetches
 * that JSON and dispatches on ``view_kind`` to a registered renderer. Unknown
 * kinds, fetch/CORS failures, and version mismatches degrade to a readable
 * placeholder — never a white screen or a broken <img>.
 *
 * NOTE: this fetch used to go cross-origin to R2 and was therefore CORS-gated
 * (unlike the <img>-based svg/mesh paths), so it only worked while a GET CORS
 * rule was live on the bucket — which it wasn't, for any tenant custom domain
 * (KAN-768). Since then ``view_spec_url`` is a relative URL served by the API
 * itself, so the fetch is same-origin and no CORS rule is consulted. The
 * empty/failed-body branch stays: a 404 (swept object, expired grant) still has
 * to degrade to the placeholder rather than a white screen.
 */

import i18next from "i18next";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ViewSpec } from "@/api/types";
import { formatDecimal } from "@/lib/utils";

import { renderMessage } from "@/components/WarningsPanel";

import { InteractiveControls } from "./InteractiveControls";
import { rotationOfInputs, type InteractiveBinding } from "./interactiveInputs";

/** Hodnota „317.7" nebo „120 × 80.5" ze serveru — čísla bez jazyka (KAN-2017). */
const NUMERIC_VALUE = /^-?\d+(?:\.\d+)?(?: × -?\d+(?:\.\d+)?)*$/;

/** Číslo z modulu podle jazyka stránky (KAN-1644: „445,66 mm", ne „445.66").
 *  KAN-2017 — server posílá hodnotu metriky jako text s desetinnou tečkou
 *  („317.7", „120 × 80.5"); i ta se čte v jazyce stránky („317,7"). */
const formatMetric = (value: unknown): string => {
  if (typeof value === "number") return formatDecimal(value, i18next.language);
  const text = String(value);
  if (!NUMERIC_VALUE.test(text)) return text;
  return text
    .split(" × ")
    .map((part) => formatDecimal(part, i18next.language))
    .join(" × ");
};

const SUPPORTED_VERSION = 1;

/** Wrapper view_kind: ``data.views`` holds several per-block view-specs the admin
 *  authored; we render them stacked. Mirrors BE ``COMPOSITE_VIEW_KIND``. */
const COMPOSITE_VIEW_KIND = "composite";

/** three.js mesh viewer — lazy so the heavy 3D bundle only loads when a module
 *  actually emits a ``model3d`` view-spec (the common metrics/image/chart paths
 *  pay nothing). Reused from the upload preview so the 3D look is identical. */
const LazyMeshPreview = lazy(() => import("@/components/MeshPreview"));

/** Sentinel a module can put in ``data.src`` to reference the uploaded file
 *  instead of embedding it — the platform resolves it to the source's URL. */
const SOURCE_REF = "@source";

interface InteractiveViewerProps {
  url: string;
  /** Presigned URL of the uploaded source file. A view-spec whose ``data.src``
   *  equals ``"@source"`` resolves to this, so a module can reference the
   *  uploaded asset (e.g. its 3D model) without re-embedding the bytes. */
  sourceUrl?: string | null;
  /** Live ``fieldKey → hex`` map (``resolveBoundColors``). A ``model3d`` block that
   *  declares ``data.color_field`` tints to ``boundColors[color_field]`` — the
   *  customer's chosen option colour — falling back to the block's static
   *  ``data.color`` then the viewer default. Absent in contexts with no chosen
   *  colour (the mesh keeps its static / default tint). */
  boundColors?: Record<string, string>;
  /** Interactive inputs of this part (KAN-1703): each block draws the controls
   *  declared on its ``view_kind`` and reports a change through ``onChange``.
   *  Absent = the viewer only shows, as before. */
  interactive?: InteractiveBinding;
}

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; spec: ViewSpec };

function Placeholder({ label }: { label: string }): JSX.Element {
  return (
    <div
      className="flex aspect-[4/3] items-center justify-center rounded-md border border-border bg-surface text-sm text-ink-4"
      data-testid="interactive-viewer-placeholder"
    >
      {label}
    </div>
  );
}

interface MetricRow {
  label: string;
  /** KAN-1818 — anglický popisek (API ho posílá, když ho modul zná). */
  label_en?: string | null;
  value: string | number;
  unit?: string | null;
  /** KAN-1790 — varování analyzátoru ``{code, params}``; vykreslí se lidsky
   *  (tentýž překlad jako panel varování), ne jako surový objekt. */
  warnings?: { code: string; params: Record<string, unknown> }[];
}

/** Popisek řádku v jazyce zobrazení; bez překladu zůstává výchozí. */
export function metricLabel(
  row: { label: string; label_en?: string | null },
  language: string,
): string {
  return language.toLowerCase().startsWith("en") && row.label_en
    ? row.label_en
    : row.label;
}

/** Phase-1 ``metrics`` renderer: an interactive key/value table. Exported so the
 *  admin schema editor can preview the EXACT component the customer sees. */
export function MetricsView({ data }: { data: Record<string, unknown> }): JSX.Element {
  const { t } = useTranslation("quote");
  const [selected, setSelected] = useState<number | null>(null);
  const rows: MetricRow[] = Array.isArray((data as { metrics?: unknown }).metrics)
    ? ((data as { metrics: MetricRow[] }).metrics)
    : [];

  if (rows.length === 0) {
    return (
      <Placeholder
        label={t("interactiveViewer.empty", {
          defaultValue: "Modul nevrátil žádná data k zobrazení.",
        })}
      />
    );
  }

  return (
    <div
      className="overflow-hidden rounded-md border border-border bg-surface"
      data-testid="interactive-viewer-metrics"
    >
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={`${row.label}-${i}`}
              onMouseEnter={() => setSelected(i)}
              onMouseLeave={() =>
                setSelected((cur) => (cur === i ? null : cur))
              }
              onClick={() => setSelected((cur) => (cur === i ? null : i))}
              className={
                "cursor-default border-b border-border last:border-0 " +
                (selected === i ? "bg-surface-2" : "")
              }
              data-testid={`interactive-viewer-row-${i}`}
            >
              <td className="px-4 py-2 text-ink-2">
                {metricLabel(row, i18next.language)}
              </td>
              <td className="px-4 py-2 text-right font-medium tabular-nums text-ink">
                {row.warnings && row.warnings.length > 0 ? (
                  <ul className="space-y-1 text-left font-normal" data-testid={`interactive-viewer-warnings-${i}`}>
                    {row.warnings.map((w, wi) => (
                      <li key={`${w.code}-${wi}`}>{renderMessage(w, t, i18next.language)}</li>
                    ))}
                  </ul>
                ) : (
                  <>
                    {formatMetric(row.value)}
                    {row.unit ? (
                      <span className="ml-1 text-ink-3">{row.unit}</span>
                    ) : null}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** ``image`` renderer: a static picture the module produced (``data.src`` is a
 *  presigned URL or a ``data:`` URI). The module owns the content; the platform
 *  only frames it. */
export function ImageView({ data }: { data: Record<string, unknown> }): JSX.Element {
  const { t } = useTranslation("quote");
  const src = typeof data.src === "string" ? data.src : "";
  const alt = typeof data.alt === "string" ? data.alt : "";
  if (!src) {
    return (
      <Placeholder
        label={t("interactiveViewer.empty", {
          defaultValue: "Modul nevrátil žádná data k zobrazení.",
        })}
      />
    );
  }
  return (
    <div
      className="overflow-hidden rounded-md border border-border bg-surface"
      data-testid="interactive-viewer-image"
    >
      <img
        src={src}
        alt={alt}
        className="mx-auto block max-h-[520px] w-full object-contain"
      />
    </div>
  );
}

/** ``model3d`` renderer: an interactive 3D model the module emits (``data.src``
 *  is a presigned URL or a ``data:`` URI; STL today, matching the platform's
 *  STLLoader). Reuses the upload-preview ``MeshPreview`` so the viewer is the
 *  same one customers already know.
 *
 *  Colour resolves bound → static → default: a ``data.color_field`` binding looks
 *  the customer's chosen option hex up in ``boundColors``; falling back to the
 *  block's static ``data.color``, then ``MeshPreview``'s default violet.
 *
 *  Orientation is the customer's ``quarter_turns_3d`` pick when the part
 *  declares one (KAN-1703), shown the moment it changes; otherwise identity. */
export function Model3dView({
  data,
  boundColors,
  interactive,
}: {
  data: Record<string, unknown>;
  boundColors?: Record<string, string>;
  interactive?: InteractiveBinding;
}): JSX.Element {
  const { t } = useTranslation("quote");
  const src = typeof data.src === "string" ? data.src : "";
  const field = typeof data.color_field === "string" ? data.color_field : "";
  const bound = field ? boundColors?.[field] : undefined;
  const staticColor = typeof data.color === "string" ? data.color : undefined;
  const color = bound ?? staticColor;
  if (!src) {
    return (
      <Placeholder
        label={t("interactiveViewer.empty", {
          defaultValue: "Modul nevrátil žádná data k zobrazení.",
        })}
      />
    );
  }
  return (
    <div
      className="overflow-hidden rounded-md border border-border bg-surface"
      data-testid="interactive-viewer-model3d"
    >
      <Suspense
        fallback={
          <Placeholder
            label={t("interactiveViewer.loading", { defaultValue: "Načítám náhled…" })}
          />
        }
      >
        <LazyMeshPreview
          src={src}
          rotation={rotationOfInputs(interactive?.inputs) ?? { x: 0, y: 0, z: 0 }}
          color={color}
        />
      </Suspense>
      <InteractiveControls binding={interactive} viewKind="model3d" />
    </div>
  );
}

interface ChartDatum {
  label: string;
  value: number;
  color?: string;
}

const CHART_PALETTE = [
  "#6366f1", "#22c55e", "#f59e0b", "#ef4444",
  "#06b6d4", "#a855f7", "#84cc16", "#ec4899",
];

function chartSeries(data: Record<string, unknown>): ChartDatum[] {
  const raw = Array.isArray((data as { series?: unknown }).series)
    ? ((data as { series: unknown[] }).series)
    : [];
  const out: ChartDatum[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const value = typeof o.value === "number" ? o.value : Number(o.value);
    if (!Number.isFinite(value)) continue;
    out.push({
      label: metricLabel(
        {
          label: String(o.label ?? ""),
          label_en: typeof o.label_en === "string" ? o.label_en : null,
        },
        i18next.language,
      ),
      value,
      color: typeof o.color === "string" ? o.color : undefined,
    });
  }
  return out;
}

const colorAt = (d: ChartDatum, i: number): string =>
  d.color ?? CHART_PALETTE[i % CHART_PALETTE.length];

function BarChart({ rows, unit }: { rows: ChartDatum[]; unit: string }): JSX.Element {
  const W = 320, H = 200, padL = 8, padR = 8, padTop = 16, padBottom = 28;
  const max = Math.max(...rows.map((r) => r.value), 0) || 1;
  const plotW = W - padL - padR;
  const plotH = H - padTop - padBottom;
  const slot = plotW / rows.length;
  const barW = Math.max(2, slot * 0.6);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      {rows.map((r, i) => {
        const h = (r.value / max) * plotH;
        const x = padL + i * slot + (slot - barW) / 2;
        const y = padTop + (plotH - h);
        return (
          <g key={`${r.label}-${i}`}>
            <rect x={x} y={y} width={barW} height={h} rx={2} fill={colorAt(r, i)} />
            <text x={x + barW / 2} y={y - 3} textAnchor="middle" className="fill-ink-2" fontSize="8">
              {formatMetric(r.value)}{unit ? ` ${unit}` : ""}
            </text>
            <text x={x + barW / 2} y={H - 16} textAnchor="middle" className="fill-ink-3" fontSize="8">
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ rows, unit }: { rows: ChartDatum[]; unit: string }): JSX.Element {
  const W = 320, H = 200, padL = 8, padR = 8, padTop = 16, padBottom = 28;
  const max = Math.max(...rows.map((r) => r.value), 0) || 1;
  const plotW = W - padL - padR;
  const plotH = H - padTop - padBottom;
  const step = rows.length > 1 ? plotW / (rows.length - 1) : 0;
  const pt = (r: ChartDatum, i: number): [number, number] => [
    padL + i * step,
    padTop + (plotH - (r.value / max) * plotH),
  ];
  const points = rows.map(pt);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      <polyline
        fill="none"
        stroke={CHART_PALETTE[0]}
        strokeWidth="2"
        points={points.map(([x, y]) => `${x},${y}`).join(" ")}
      />
      {rows.map((r, i) => {
        const [x, y] = points[i];
        return (
          <g key={`${r.label}-${i}`}>
            <circle cx={x} cy={y} r={2.5} fill={CHART_PALETTE[0]} />
            <text x={x} y={y - 5} textAnchor="middle" className="fill-ink-2" fontSize="8">
              {formatMetric(r.value)}{unit ? ` ${unit}` : ""}
            </text>
            <text x={x} y={H - 16} textAnchor="middle" className="fill-ink-3" fontSize="8">
              {r.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function PieChart({ rows }: { rows: ChartDatum[] }): JSX.Element {
  const total = rows.reduce((s, r) => s + r.value, 0) || 1;
  const cx = 70, cy = 70, rad = 60;
  let acc = 0;
  const arc = (frac: number): [number, number] => [
    cx + rad * Math.cos(2 * Math.PI * frac - Math.PI / 2),
    cy + rad * Math.sin(2 * Math.PI * frac - Math.PI / 2),
  ];
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 140 140" className="h-36 w-36 shrink-0" role="img">
        {rows.map((r, i) => {
          const start = acc / total;
          acc += r.value;
          const end = acc / total;
          const [x1, y1] = arc(start);
          const [x2, y2] = arc(end);
          const large = end - start > 0.5 ? 1 : 0;
          return (
            <path
              key={`${r.label}-${i}`}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${rad} ${rad} 0 ${large} 1 ${x2} ${y2} Z`}
              fill={colorAt(r, i)}
            />
          );
        })}
      </svg>
      <ul className="space-y-1 text-xs">
        {rows.map((r, i) => (
          <li key={`${r.label}-${i}`} className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorAt(r, i) }} />
            <span className="text-ink-2">{r.label}</span>
            <span className="font-medium text-ink">{formatMetric(r.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** ``chart`` renderer: a bar / line / pie chart from numeric ``data.series``.
 *  Hand-rolled SVG (no charting dependency); the module supplies the numbers. */
export function ChartView({ data }: { data: Record<string, unknown> }): JSX.Element {
  const { t } = useTranslation("quote");
  const rows = chartSeries(data);
  const kind = typeof data.chart === "string" ? data.chart : "bar";
  const unit = typeof data.unit === "string" ? data.unit : "";
  const title = typeof data.title === "string" ? data.title : "";
  if (rows.length === 0) {
    return (
      <Placeholder
        label={t("interactiveViewer.empty", {
          defaultValue: "Modul nevrátil žádná data k zobrazení.",
        })}
      />
    );
  }
  return (
    <div
      className="rounded-md border border-border bg-surface p-3"
      data-testid="interactive-viewer-chart"
    >
      {title ? <p className="mb-2 text-sm font-medium text-ink">{title}</p> : null}
      {kind === "pie" ? (
        <PieChart rows={rows} />
      ) : kind === "line" ? (
        <LineChart rows={rows} unit={unit} />
      ) : (
        <BarChart rows={rows} unit={unit} />
      )}
    </div>
  );
}

interface RenderContext {
  boundColors?: Record<string, string>;
  interactive?: InteractiveBinding;
}

const VIEW_KIND_RENDERERS: Record<
  string,
  (data: Record<string, unknown>, ctx: RenderContext) => JSX.Element
> = {
  metrics: (data) => <MetricsView data={data} />,
  image: (data) => <ImageView data={data} />,
  model3d: (data, ctx) => (
    <Model3dView data={data} boundColors={ctx.boundColors} interactive={ctx.interactive} />
  ),
  chart: (data) => <ChartView data={data} />,
};

/** Resolve the ``@source`` reference in ``data.src`` to the uploaded file's URL
 *  so a block can point the viewer at the customer's own upload without
 *  embedding the bytes. */
function resolveSource(
  data: Record<string, unknown>,
  sourceUrl?: string | null,
): Record<string, unknown> {
  return data.src === SOURCE_REF && sourceUrl ? { ...data, src: sourceUrl } : data;
}

/** Render one view-spec by ``view_kind``; unknown kinds → placeholder. */
function RenderOne({
  spec,
  sourceUrl,
  boundColors,
  interactive,
}: {
  spec: ViewSpec;
  sourceUrl?: string | null;
  boundColors?: Record<string, string>;
  interactive?: InteractiveBinding;
}): JSX.Element {
  const { t } = useTranslation("quote");
  const renderer = VIEW_KIND_RENDERERS[spec.view_kind];
  if (!renderer) {
    return (
      <Placeholder
        label={t("interactiveViewer.unknownKind", {
          defaultValue: "Tento typ náhledu zatím není podporován.",
        })}
      />
    );
  }
  return renderer(resolveSource(spec.data ?? {}, sourceUrl), { boundColors, interactive });
}

/** ``composite`` renderer: several authored blocks stacked vertically. Each
 *  sub-view is a full ``{view_kind, version, data}`` spec rendered by its own
 *  renderer (with independent ``@source`` resolution). */
function CompositeView({
  data,
  sourceUrl,
  boundColors,
  interactive,
}: {
  data: Record<string, unknown>;
  sourceUrl?: string | null;
  boundColors?: Record<string, string>;
  interactive?: InteractiveBinding;
}): JSX.Element {
  const { t } = useTranslation("quote");
  const views = Array.isArray((data as { views?: unknown }).views)
    ? ((data as { views: unknown[] }).views)
    : [];
  const specs = views.filter(
    (v): v is ViewSpec =>
      !!v && typeof v === "object" && typeof (v as ViewSpec).view_kind === "string",
  );
  if (specs.length === 0) {
    return (
      <Placeholder
        label={t("interactiveViewer.empty", {
          defaultValue: "Modul nevrátil žádná data k zobrazení.",
        })}
      />
    );
  }
  return (
    <div className="space-y-3" data-testid="interactive-viewer-composite">
      {specs.map((spec, i) => (
        <RenderOne
          key={i}
          spec={spec}
          sourceUrl={sourceUrl}
          boundColors={boundColors}
          interactive={interactive}
        />
      ))}
    </div>
  );
}

export function InteractiveViewer({
  url,
  sourceUrl,
  boundColors,
  interactive,
}: InteractiveViewerProps): JSX.Element {
  const { t } = useTranslation("quote");
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const spec = (await res.json()) as ViewSpec;
        if (cancelled) return;
        if (
          !spec ||
          typeof spec.view_kind !== "string" ||
          spec.version !== SUPPORTED_VERSION
        ) {
          setState({ status: "error" });
          return;
        }
        setState({ status: "ready", spec });
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (state.status === "loading") {
    return (
      <Placeholder
        label={t("interactiveViewer.loading", {
          defaultValue: "Načítám náhled…",
        })}
      />
    );
  }
  if (state.status === "error") {
    return (
      <Placeholder
        label={t("interactiveViewer.error", {
          defaultValue: "Náhled se nepodařilo načíst.",
        })}
      />
    );
  }

  if (state.spec.view_kind === COMPOSITE_VIEW_KIND) {
    return (
      <CompositeView
        data={state.spec.data ?? {}}
        sourceUrl={sourceUrl}
        boundColors={boundColors}
        interactive={interactive}
      />
    );
  }
  return (
    <RenderOne
      spec={state.spec}
      sourceUrl={sourceUrl}
      boundColors={boundColors}
      interactive={interactive}
    />
  );
}

export default InteractiveViewer;
